/**
 * ApiKeyModal
 *
 * Anthropic Claude API のキーをユーザーから受け取るモーダル。
 *
 * - 初回起動時：dismissable=false → 閉じられない（キーが無いとアプリが動かないので）
 * - 設定画面から開いた時：dismissable=true → キャンセル可能
 *
 * セキュリティ：
 * - 入力欄はデフォルトで type="password"（覗き見対策）
 * - 「表示」トグルで一時的に平文表示
 * - サブミット時はトリムしてから保存
 */

import { useState } from 'react';
import type { FormEvent } from 'react';

import { isLikelyValidApiKey } from '../lib/storage';

import styles from './ApiKeyModal.module.css';

export interface ApiKeyModalProps {
  /** 既存のキー（編集モード用、未設定時は undefined） */
  initialValue?: string;
  /** キャンセル可能かどうか（初回起動時は false） */
  dismissable: boolean;
  /** 保存ボタン押下時 */
  onSave: (key: string) => void;
  /** キャンセル/閉じる時（dismissable=true のみ呼ばれる） */
  onDismiss?: () => void;
}

export function ApiKeyModal({
  initialValue = '',
  dismissable,
  onSave,
  onDismiss,
}: ApiKeyModalProps) {
  const [value, setValue] = useState(initialValue);
  const [visible, setVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const trimmed = value.trim();

    if (trimmed.length === 0) {
      setErrorMessage('APIキーを入力してください。');
      return;
    }

    if (!isLikelyValidApiKey(trimmed)) {
      setErrorMessage('APIキーの形式が正しくありません（"sk-ant-" で始まる文字列を入力してください）。');
      return;
    }

    setErrorMessage(null);
    onSave(trimmed);
  };

  return (
    <div className={styles.backdrop} role="dialog" aria-modal="true" aria-labelledby="api-key-title">
      <form className={styles.dialog} onSubmit={handleSubmit}>
        <h2 id="api-key-title" className={styles.title}>
          API KEY REQUIRED
        </h2>

        <p className={styles.description}>
          ScouterLensは <strong>Anthropic Claude API</strong> を使ってカメラ映像を解析します。
          初回はAPIキーの登録が必要です。
          キーは
          {' '}<a href="https://console.anthropic.com/" target="_blank" rel="noreferrer">Anthropic Console</a>{' '}
          から発行できます（<code>sk-ant-</code> で始まる文字列）。
        </p>

        <label className={styles.fieldLabel}>
          API KEY
          <div className={styles.inputWrapper}>
            <input
              className={styles.input}
              type={visible ? 'text' : 'password'}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="sk-ant-..."
              autoComplete="off"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              // モバイルでもサジェストを抑制
              inputMode="text"
            />
            <button
              type="button"
              className={styles.toggleVisibility}
              onClick={() => setVisible((v) => !v)}
              aria-label={visible ? 'APIキーを隠す' : 'APIキーを表示する'}
            >
              {visible ? 'HIDE' : 'SHOW'}
            </button>
          </div>
        </label>

        <p className={styles.errorMessage} role={errorMessage ? 'alert' : undefined}>
          {errorMessage ?? ' ' /* 高さ確保用のNBSP */}
        </p>

        <div className={styles.actions}>
          {dismissable && (
            <button
              type="button"
              className={`${styles.button} ${styles.buttonSecondary}`}
              onClick={() => onDismiss?.()}
            >
              CANCEL
            </button>
          )}
          <button
            type="submit"
            className={`${styles.button} ${styles.buttonPrimary}`}
            disabled={value.trim().length === 0}
          >
            SAVE
          </button>
        </div>

        <p className={styles.notice}>
          ⚠ APIキーは端末のlocalStorageに保存されます（暗号化なし、自己責任）。
          公開環境や共有端末での利用は推奨しません。
        </p>
      </form>
    </div>
  );
}
